import type { MfgPurchaseOrder, MfgPurchaseOrderStatus } from "./purchaseOrderTypes";

export type MfgPoOrigin = "purchaser" | "plant_request" | "prediction";
export type PlantRequestPhase = "submitted" | "acknowledged" | "ordered";

const ISSUED_OR_LATER: ReadonlySet<MfgPurchaseOrderStatus> = new Set([
  "issued",
  "confirmed",
  "partially_received",
  "received",
]);

export function poOrigin(order: Pick<MfgPurchaseOrder, "origin"> | { origin?: string | null }): MfgPoOrigin {
  if (order.origin === "plant_request" || order.origin === "prediction") return order.origin;
  return "purchaser";
}

export function isPlantMaterialRequest(
  order: Pick<MfgPurchaseOrder, "origin"> | { origin?: string | null },
): boolean {
  return poOrigin(order) === "plant_request";
}

export function isPoIssuedOrLater(status: MfgPurchaseOrderStatus | string): boolean {
  return ISSUED_OR_LATER.has(status as MfgPurchaseOrderStatus);
}

export function plantRequestPhase(
  request: Pick<
    MfgPurchaseOrder,
    "id" | "origin" | "status" | "acknowledged_at" | "source_request_id"
  >,
  allOrders: ReadonlyArray<Pick<MfgPurchaseOrder, "id" | "status" | "source_request_id">> = [],
): PlantRequestPhase | null {
  if (!isPlantMaterialRequest(request)) return null;
  if (request.status === "cancelled") return null;
  if (isPoIssuedOrLater(request.status)) return "ordered";
  const childIssued = allOrders.some(
    (o) => o.source_request_id === request.id && isPoIssuedOrLater(o.status),
  );
  if (childIssued) return "ordered";
  if (request.acknowledged_at) return "acknowledged";
  return "submitted";
}

export function formatExpectedTimeframe(isoDate: string | null | undefined): string | null {
  const raw = (isoDate ?? "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (!Number.isFinite(dt.getTime())) return null;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function plantRequestStatusCopy(
  phase: PlantRequestPhase,
  opts?: { expectedDeliveryOn?: string | null; poNumber?: string },
): string {
  if (phase === "submitted") {
    return "You submitted this material request. It has not been acknowledged or ordered yet.";
  }
  if (phase === "acknowledged") {
    return "This material request has been acknowledged. It has not been ordered yet.";
  }
  const when = formatExpectedTimeframe(opts?.expectedDeliveryOn);
  const po = (opts?.poNumber ?? "").trim();
  const head = po
    ? `The purchase order for this material request (${po}) is in.`
    : "The purchase order for this material request is in.";
  return when ? `${head} Expected ${when}.` : head;
}

export function purchaserReviewCopy(count: number): string {
  if (count <= 0) return "";
  if (count === 1) return "There's a material request. Please review.";
  return `There are ${count} material requests. Please review.`;
}

export type PlantRequestDraftLine = {
  material_id: string | null;
  material_name: string;
  quantity: string;
};

export function filledPlantRequestLines<T extends PlantRequestDraftLine>(lines: readonly T[]): T[] {
  return lines.filter((l) => {
    if (!l.material_name.trim()) return false;
    const qty = Number(l.quantity);
    return Number.isFinite(qty) && qty > 0;
  });
}

export function canSubmitPlantMaterialRequest(
  note: string,
  lines: readonly PlantRequestDraftLine[],
): boolean {
  return note.trim().length > 0 || filledPlantRequestLines(lines).length > 0;
}

export function attachCatalogMatch<T extends PlantRequestDraftLine>(
  line: T,
  materials: ReadonlyArray<{ id: string; name: string; code?: string | null; plant_id?: string | null }>,
  plantId: string,
): T {
  if (line.material_id) return line;
  const name = line.material_name.trim().toLowerCase();
  if (!name) return line;
  const existing = materials.find((m) => {
    if (plantId && m.plant_id && m.plant_id !== plantId) return false;
    return (
      m.name.trim().toLowerCase() === name ||
      (m.code ?? "").trim().toLowerCase() === name
    );
  });
  return existing ? { ...line, material_id: existing.id, material_name: existing.name } : line;
}

export function plantRequestInboxBlurb(
  order: Pick<MfgPurchaseOrder, "request_note" | "lines">,
): string {
  const note = (order.request_note ?? "").trim();
  const items = (order.lines ?? [])
    .filter((l) => l.material_name.trim())
    .map((l) => `${l.quantity} ${l.material_name}`.trim())
    .join(", ");
  if (note && items) return `${note} · ${items}`;
  return note || items || "Material request";
}

export function isUnacknowledgedPlantRequest(
  order: Pick<MfgPurchaseOrder, "origin" | "status" | "acknowledged_at">,
  allOrders: ReadonlyArray<Pick<MfgPurchaseOrder, "id" | "status" | "source_request_id">> = [],
): boolean {
  return plantRequestPhase(order as MfgPurchaseOrder, allOrders) === "submitted";
}

export function countUnacknowledgedPlantRequests(
  orders: ReadonlyArray<
    Pick<MfgPurchaseOrder, "id" | "origin" | "status" | "acknowledged_at" | "source_request_id">
  >,
): number {
  return orders.filter((o) => plantRequestPhase(o, orders) === "submitted").length;
}

/** Status strip: open requests until received or archived. */
export function plantRequestAlertOrders<
  T extends Pick<
    MfgPurchaseOrder,
    "id" | "origin" | "status" | "acknowledged_at" | "source_request_id" | "expected_delivery_on" | "po_number"
  >,
>(orders: readonly T[]): T[] {
  return orders.filter((o) => {
    if (o.status === "cancelled" || o.status === "received") return false;
    const phase = plantRequestPhase(o, orders);
    return phase === "submitted" || phase === "acknowledged" || phase === "ordered";
  });
}
